import React, { useEffect, useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import insertTrackableLinkPopup from 'components/Dashboard/helpers/insertTrackableLinkPopup';
import { insertListTinyEditor } from 'components/Dashboard/utilities/insertListTinyEditor';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import { Portal } from 'react-portal';
import strip_tags from 'components/Dashboard/utilities/stripTags';
import { createLinkDetectionHandler, detectAndReplaceLinks } from 'components/Dashboard/helpers/linkDetectFunctions';
import applyTextColor from 'components/Dashboard/utilities/applyTextColor';
import useEditorAttachments, { attachmentContentStyles } from 'components/Dashboard/hooks/useEditorAttachments';

export const blockquoteStyles = `.mce-content-body:not([dir=rtl]) blockquote {
  border-left: 2px solid #0094cc !important;
  margin: 0 !important;
  padding: 0 0.5em !important;
  background-color: rgba(0, 148, 204, 0.05) !important;
  color: rgba(0, 18, 43, 0.8) !important;
}`;

// Style constants for better readability
const getContentStyles = ({ reportWidget, minHeight, isMobile, addVerticalScroll, additionalStyles = '' }) => {
  const fontImport = `@import url('https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,300;0,400;0,700;0,900;1,400&family=Work+Sans:wght@300&display=swap');`;

  const placeholderStyles = `.mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
    color: #bababa;
  }`;

  const reportWidgetPlaceholderStyles = reportWidget
    ? `.mce-content-body[data-mce-placeholder]:not(.mce-visualblocks) {
      min-height: ${minHeight - 75}px; 
      cursor: text;
    }`
    : '';

  const paragraphStyles = reportWidget
    ? `p {
      margin: 0; 
      line-height: 1.4; 
      color: #676a75;
    }`
    : '';

  const linkStyles = `a {
    color: ${reportWidget ? '#676a75' : '#0094cc'}; 
    text-decoration: ${reportWidget ? 'none' : 'underline'};
  };
  .mce-content-body [data-mce-selected="inline-boundary"] {
    background-color: transparent !important;
  }`;

  const listStyles = `ol, ul {
    padding-left: 24px;
  }`;

  const bodyStyles = `body { 
    font-family: Lato, sans-serif; 
    font-size: 16px; 
    padding-left: 12px; 
    margin-top: 12px !important;  
    line-height: 1.6;  
    margin-left: ${isMobile ? 0 : 15}px; 
    color: #00122b; 
    ${addVerticalScroll ? 'overflow-y: auto !important' : ''};
  }`;

  return [
    fontImport,
    placeholderStyles,
    reportWidgetPlaceholderStyles,
    paragraphStyles,
    linkStyles,
    listStyles,
    bodyStyles,
    blockquoteStyles,
    additionalStyles,
  ]
    .filter(Boolean)
    .join(' ');
};

const DescriptionTinyEditor = React.forwardRef((props, ref) => {
  const {
    value,
    setValue,
    index,
    placeholder = '',
    onChangeFunction,
    reportWidget,
    additionalSetupFunction,
    editorToEdit: editorToEditProps,
    minHeight = 223,
    maxHeight,
    additionalButtons = '',
    keyupfunction,
    modifyItemLayout,
    onInitFunction,
    addVerticalScroll,
    disabled,
    toolbarLocation,
    contentStyle,
    keyDownFunction,
    defaultValue,
    showToolbar = true,
    customClass,
    newline_behavior,
    forced_root_block = 'p',
    forced_root_block_attrs,
    hideSkeleton,
    autoresize_bottom_margin,
    defaultColor = '676a75',
    colorPickerPosition,
    additionalStyles,
    disableAutofocus = false,
    customButtons,
    enableContextMenu = true,
    enableAttachments,
  } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [openColorPicker, setOpenColorPicker] = useState(false);
  const [bookmark, setBookmark] = useState(null);
  const [selectedColor, setSelectedColor] = useState(defaultColor);
  const editorToEditRef = useRef(null);
  const editorToEdit = editorToEditProps ?? editorToEditRef;
  const onChange = (content, editor) => {
    if (onChangeFunction) {
      onChangeFunction(content, editor);
    } else {
      setValue(content);
    }
  };
  const isMobile = useIsMobile(480);
  const { setupAttachments, attachModal, dropOverlay } = useEditorAttachments();
  let plugins = [
    'lists',
    'image',
    'charmap',
    'preview',
    'anchor',
    'visualblocks',
    'code',
    'fullscreen',
    'insertdatetime',
    'media',
    'table',
    'code',
    'help',
    'wordcount',
    'autoresize',
  ];
  if (!reportWidget) {
    plugins = [...plugins, 'link', 'autolink'];
  }

  useEffect(() => {
    let buttons = document.querySelectorAll('.tox-tbtn');
    buttons.forEach((btn) => {
      btn.title = '';
    });
  }, [ref?.current]);

  const rgbaToHex = (rgba) => {
    const match = rgba.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!match) return null;

    const [r, g, b] = match.map(Number);
    return [r, g, b]
      .map((x) => x.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  };

  return (
    <>
      <div
        className={`position-relative tiny-editor-toolbar${showToolbar ? '-shown' : '-hidden'} ${
          customClass ? customClass : ''
        } description-tiny-editor`}
        style={{ minHeight: `${minHeight}px` }}
      >
        {enableAttachments && dropOverlay}
        {isLoading && !hideSkeleton && (
          <div
            className={`bg-white border rounded position-absolute z-max w-100 left-0 top-0 px-4 py-3 mt-0 tox-editor-container-skeleton`}
            style={{ height: `${minHeight}px` }}
          >
            <SkeletonTheme baseColor='#fff'>
              <Skeleton width={'100%'} height={'19px'} />
              <Skeleton width={'80%'} height={'19px'} />
            </SkeletonTheme>
          </div>
        )}
        <Editor
          ref={ref}
          modifyItemLayout={modifyItemLayout}
          selectedColor={selectedColor}
          tinymceScriptSrc={import.meta.env.VITE_APP_APPURL + '/tinymce/tinymce.min.js'}
          licenseKey='gpl'
          disabled={disabled}
          onInit={(evt, editor) => {
            if (editorToEdit) {
              editorToEdit.current = editor;
            }
            setIsLoading(false);
            if (onInitFunction) {
              onInitFunction(editor);
            }
          }}
          onFocus={(evt, editor) => {
            editor.selection.select(editor.getBody(), true);
            editor.selection.collapse(false);
            if (selectedColor !== '676a75') {
              applyTextColor(editor, selectedColor);
            }
          }}
          defaultValue={defaultValue}
          value={value}
          init={{
            autoresize_bottom_margin: autoresize_bottom_margin ?? 0,
            height: '100%',
            menubar: false,
            placeholder,
            paste_preprocess: function (pl, o) {
              o.content = strip_tags(o.content, '<b><u><i><p><li><strong><br><ol>');
            },
            plugins,
            browser_spellcheck: true,
            toolbar:
              customButtons ??
              `bold | italic | strikethrough ${
                reportWidget ? '' : '| customLink'
              } | numlist | bullist | customColorPicker | blockquote  ${
                reportWidget ? '| insertMacrosButton' : ''
              } ${additionalButtons}${enableAttachments ? ' | attachmentBtn' : ''}`,
            content_style:
              contentStyle ??
              getContentStyles({
                reportWidget,
                minHeight,
                isMobile,
                addVerticalScroll,
                additionalStyles: enableAttachments
                  ? `${additionalStyles ?? ''} ${attachmentContentStyles}`
                  : additionalStyles,
              }),
            textcolor_rows: '3',
            extended_valid_elements: enableAttachments
              ? 'span[*],a[href|target=_blank|class|style|rel|contenteditable]'
              : 'span[*],a[href|target=_blank]',
            toolbar_location: toolbarLocation ?? 'top',
            min_height: minHeight,
            max_height: maxHeight,
            link_assume_external_targets: 'http',
            convert_urls: false,
            default_link_target: '_blank',
            target_list: [{ title: 'New page', value: '_blank' }],
            link_title: false,
            anchor_top: false,
            anchor_bottom: false,
            newline_behavior: newline_behavior ?? 'default',
            forced_root_block: forced_root_block,
            forced_root_block_attrs: forced_root_block_attrs,
            contextmenu: enableContextMenu === false ? false : undefined,
            setup: function (editor) {
              editor.on('init', function (e) {
                if (index === 0 && !disableAutofocus) {
                  editor.selection.select(editor.getBody(), true);
                  editor.selection.collapse(false);
                  editor.focus();
                  const body = editor.getBody();
                  if (body) {
                    const caretSpans = body.querySelectorAll('span[id="_mce_caret"]');
                    caretSpans.forEach((span) => span.remove());
                  }
                }
              });
              editor.on('NodeChange', function (e) {
                if (e.element && e.element.style.color) {
                  const color = rgbaToHex(e.element.style.color);
                  const parent = editor?.editorContainer?.parentElement;
                  const btn = parent?.querySelectorAll('.tox-tbtn');
                  const customColorBtn =
                    Array.from(btn || []).find((b) => b.getAttribute('data-mce-name') === 'customcolorpicker') ??
                    btn?.[4];
                  if (color !== selectedColor) {
                    setSelectedColor(color);
                    if (customColorBtn) {
                      let btn = customColorBtn.querySelector('.tox-icon');
                      btn.style.backgroundColor = `#${color}`;
                    }
                  }
                }
                if (e.element?.tagName === 'P') {
                  const toolbarButtons = editor.editorContainer.querySelectorAll('.tox-tbtn');
                  toolbarButtons[2]?.classList.remove('tox-tbtn--enabled');
                  toolbarButtons[3]?.classList.remove('tox-tbtn--enabled');
                }
              });
              editor.on('ExecCommand', function (e) {
                if (e.command === 'mceLink') {
                  let dialog = document.getElementsByClassName('tox-dialog');
                  let dialogBackdrop = document.getElementsByClassName('tox-dialog-wrap__backdrop');
                  setTimeout(() => {
                    insertTrackableLinkPopup(dialog, dialogBackdrop, editor, true);
                  }, 10);
                }
              });

              // Handle blockquote command to properly wrap lists
              editor.on('BeforeExecCommand', function (e) {
                if (e.command === 'mceBlockQuote') {
                  const blockquoteButton = editor.editorContainer.querySelector('[data-mce-name="blockquote"]');
                  const selection = editor.selection;
                  e.preventDefault();
                  const body = editor.getBody();

                  //OP: remove caret spans to be able to do autolist
                  if (body) {
                    const caretSpans = body.querySelectorAll('span[id="_mce_caret"]');
                    caretSpans.forEach((span) => span.remove());
                  }

                  // Check if current selection is already inside a blockquote
                  const selectedNode = selection.getNode();
                  const blockquoteAncestor = editor.dom.getParent(selectedNode, 'blockquote');

                  if (blockquoteAncestor) {
                    // Remove blockquote wrapper around the selection
                    const blockquoteContent = blockquoteAncestor.innerHTML;
                    editor.dom.replace(editor.dom.create('div', {}, blockquoteContent), blockquoteAncestor);
                    blockquoteButton?.classList?.remove('tox-tbtn--enabled');
                    return;
                  } else if (selectedNode?.querySelector('blockquote')) {
                    // Remove blockquote wrapper around the selection
                    const blockquoteContent = selectedNode.querySelector('blockquote').innerHTML;
                    editor.dom.replace(
                      editor.dom.create('div', {}, blockquoteContent),
                      selectedNode.querySelector('blockquote')
                    );
                    blockquoteButton?.classList?.remove('tox-tbtn--enabled');
                    return;
                  } else if (selectedNode?.tagName === 'BODY' && selectedNode?.innerHTML?.includes('blockquote')) {
                    // Remove blockquote wrapper around the body
                    const blockQuoteElem = selectedNode.querySelector('blockquote');
                    const blockquoteContent = blockQuoteElem.innerHTML;
                    if (blockQuoteElem) {
                      editor.dom.replace(editor.dom.create('div', {}, blockquoteContent), blockQuoteElem);
                    }
                    blockquoteButton?.classList?.remove('tox-tbtn--enabled');
                    return;
                  }

                  if (!selection.isCollapsed()) {
                    // Wrap only selected content in blockquote
                    if (selectedNode.tagName === 'UL' || selectedNode.tagName === 'OL') {
                      // Get the range and find the common ancestor
                      const range = selection.getRng();
                      const startContainer = range.startContainer;
                      const endContainer = range.endContainer;
                      // Find the common ancestor that contains the entire selection
                      let commonAncestor = startContainer;
                      while (commonAncestor && !commonAncestor.contains(endContainer)) {
                        commonAncestor = commonAncestor.parentNode;
                      }
                      console.dir(commonAncestor, typeof commonAncestor);
                      // // If we found a common ancestor, wrap it
                      if (!!commonAncestor && !!commonAncestor !== editor.getBody()) {
                        const blockquote = editor.dom.create('blockquote', {}, commonAncestor.outerHTML);
                        editor.dom.replace(blockquote, commonAncestor);
                        blockquoteButton?.classList?.add('tox-tbtn--enabled');
                        return;
                      }
                    }
                    const selectedContent = selection.getContent();
                    const blockquote = editor.dom.create('blockquote', {}, selectedContent);
                    editor.insertContent(blockquote.outerHTML);
                    blockquoteButton?.classList?.add('tox-tbtn--enabled');
                    return;
                  }

                  // No selection, wrap the current block element (row) where caret is
                  const currentNode = selection.getNode();
                  const blockElement = editor.dom.getParent(currentNode, 'p, div, h1, h2, h3, h4, h5, h6, li');

                  if (blockElement) {
                    // For list items, wrap the entire list (ul/ol)
                    let elementToWrap = blockElement;
                    if (blockElement.tagName === 'LI') {
                      const listElement = editor.dom.getParent(blockElement, 'ul, ol');
                      if (listElement) {
                        elementToWrap = listElement;
                      }
                    }

                    // Check if the element to wrap is already inside a blockquote
                    const blockquoteAncestor = editor.dom.getParent(elementToWrap, 'blockquote');

                    if (blockquoteAncestor) {
                      // Remove blockquote wrapper around the element
                      const blockquoteContent = blockquoteAncestor.innerHTML;
                      editor.dom.replace(editor.dom.create('div', {}, blockquoteContent), blockquoteAncestor);
                      blockquoteButton?.classList?.remove('tox-tbtn--enabled');
                      return;
                    }

                    // Save current selection before wrapping
                    const bookmark = editor.selection.getBookmark();

                    // Wrap the element (or entire list for list items)
                    const blockquote = editor.dom.create('blockquote', {}, elementToWrap.outerHTML);
                    editor.dom.replace(blockquote, elementToWrap);

                    // Restore cursor position
                    editor.selection.moveToBookmark(bookmark);

                    // Focus to hide placeholder if content is minimal
                    if (elementToWrap.textContent.trim() === '') {
                      editor.focus();
                    }
                    blockquoteButton?.classList?.add('tox-tbtn--enabled');
                    return;
                  }
                }
              });
              editor.ui.registry.addToggleButton('customLink', {
                icon: 'link',
                tooltip: 'Insert Link',
                onAction: (api) => {
                  editor.execCommand('mceLink', false, 'link');
                },
                onSetup: function (api) {
                  editor.formatter.formatChanged('link', function (state) {
                    api.setActive(state);
                  });
                },
              });
              editor.ui.registry.addButton('customColorPicker', {
                icon: 'false',
                text: 'select color',
                tooltip: 'Select color',
                onAction: (api) => {
                  const currBookmark = editor.selection.bookmarkManager.getBookmark();
                  setBookmark(currBookmark);
                  setOpenColorPicker((prev) => !prev);
                },
              });

              if (enableAttachments) {
                setupAttachments(editor);
              }
              if (additionalSetupFunction) {
                additionalSetupFunction(editor, ref);
              }
              const descriptionEditor = true;
              // Add link detection handler
              const { handleKeyDown, expandCurrentLink } = createLinkDetectionHandler(editor);

              editor.on('KeyDown', (e) => {
                handleKeyDown(e);
                insertListTinyEditor(e, editor, descriptionEditor);
                if (keyDownFunction) {
                  keyDownFunction(e, editor);
                }
              });

              editor.on('KeyUp', (e) => {
                if (e.keyCode === 13 && !e.shiftKey) {
                  e.preventDefault();
                }
                insertListTinyEditor(e, editor, descriptionEditor);
                if (keyupfunction) {
                  keyupfunction(e, editor);
                }

                // Check if we're expanding an existing link
                if (window.isExpandingLink && window.currentExpandingLink) {
                  // Continue expanding the current link
                  expandCurrentLink(editor, window.currentExpandingLink);
                } else {
                  // Add link detection on keyup
                  detectAndReplaceLinks(editor);
                }
              });
            },
          }}
          onEditorChange={onChange}
        />
      </div>
      {openColorPicker && (
        <Portal node={document && document.querySelector('body')}>
          <CustomColorPicker
            setOpenColorPicker={setOpenColorPicker}
            editor={editorToEdit?.current}
            bookmark={bookmark}
            selectedColor={selectedColor}
            setSelectedColor={setSelectedColor}
            position={colorPickerPosition}
          />
        </Portal>
      )}
      {enableAttachments && attachModal}
    </>
  );
});

const CustomColorPicker = (props) => {
  const { setOpenColorPicker, editor, bookmark, selectedColor, setSelectedColor, position } = props;
  const colors = [
    '49b16a',
    'f1c410',
    'e03d2c',
    '9b6ead',
    '3997d3',
    '0b9179',
    'e67d22',
    'ba3829',
    '7c4393',
    '2470a2',
    'bdc2c7',
    '95a5a6',
    '7e8c8d',
    '676a75',
    '404453',
  ];
  const parent = editor?.editorContainer?.parentElement;
  const btn = parent?.querySelectorAll('.tox-tbtn');
  const customColorBtn =
    Array.from(btn || []).find((b) => b.getAttribute('data-mce-name') === 'customcolorpicker') ?? btn?.[4];
  const { top, left, height, width } = customColorBtn?.getBoundingClientRect() ?? {};

  const selectColor = (color) => {
    editor.focus();
    editor.selection.bookmarkManager.moveToBookmark(bookmark);
    applyTextColor(editor, color);
    setSelectedColor(color);
    if (customColorBtn) {
      let btn = customColorBtn.querySelector('.tox-icon');
      btn.style.backgroundColor = `#${color}`;
    }
    setOpenColorPicker(false);
  };

  const picker = useRef(null);
  const hideFilters = () => {
    setOpenColorPicker(false);
  };
  useOutsideElement(picker, hideFilters);

  // Clicks inside the editor happen in a separate iframe document, so they never
  // bubble to the parent document for useOutsideElement to catch - close explicitly.
  useEffect(() => {
    if (!editor) return;
    editor.on('mousedown', hideFilters);
    return () => editor.off('mousedown', hideFilters);
  }, [editor]);
  const style = {
    top: position === 'top' ? `${top - 90}px` : `${top + height}px`,
    left: `${left + width / 2 - 75}px`,
    zIndex: 100001,
  };
  return (
    <>
      <div
        className='general-close-background'
        style={{ zIndex: 100000 }}
        onClick={() => setOpenColorPicker(false)}
      />
      <div ref={picker} className='position-absolute commentary-custom-color-picker' style={style}>
        <div className='d-flex flex-wrap rounded overflow-hidden'>
          {colors.map((color) => (
            <button
              key={`picker-${color}`}
              onClick={() => selectColor(color)}
              className={`general-button color-selector ${selectedColor === color ? 'selected' : ''}`}
              style={{ backgroundColor: `#${color}` }}
            ></button>
          ))}
        </div>
      </div>
    </>
  );
};

export { CustomColorPicker };
export default DescriptionTinyEditor;
